import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaExclamationTriangle, FaSync } from 'react-icons/fa';
import useSignal from '../hooks/useSignal';
import MarketRegimeCard from '../components/Signal/MarketRegimeCard';
import AssetAnalysisCard from '../components/Signal/AssetAnalysisCard';
import '../styles/Signal.css';

// Transform API data to component-friendly format
const transformAssetData = (assetData) => {
  if (!assetData) return null;
  return {
    current_price: assetData.current_price,
    bias: assetData.bias,
    confidence: Math.round((assetData.confidence || 0) * 100),
    trend: assetData.trend?.direction,
    support: assetData.nearest_supports?.[0]?.price,
    resistance: assetData.nearest_resistances?.[0]?.price,
    notes: assetData.current_zone?.description || assetData.trend?.comment
  };
};

const Signal = () => {
  const { signal, loading, error, lastUpdated, refetch } = useSignal();

  useEffect(() => {
    document.title = 'AI Signal - Velvet AI';
  }, []);

  if (loading && !signal) {
    return (
      <div className="signal-page">
        <div className="signal-loading">
          <div className="loading-spinner" />
          <p>Loading signal data...</p>
        </div>
      </div>
    );
  }

  if (error && !signal) {
    return (
      <div className="signal-page">
        <div className="signal-error">
          <FaExclamationTriangle size={48} />
          <h2>Unable to load signal</h2>
          <p>{error}</p>
          <button className="btn-primary" onClick={refetch}>
            <FaSync /> Retry
          </button>
        </div>
      </div>
    );
  }

  const { market_context, btc, avax, combined, timestamp } = signal || {};

  // Extract values from API structure
  const suggestedAction = combined?.action?.replace(/_/g, ' ');
  const invalidations = combined?.invalidations || [];
  const managementPlan = combined?.management_plan?.trail_stop_logic;
  const suggestedPosition = combined?.suggested_position;
  const riskInfo = combined?.risk;

  const formatTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  return (
    <div className="signal-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <header className="signal-header-minimal">
          <div className="signal-header-top">
            <div className="signal-live-tag">
              <span className="live-dot-minimal" />
              Live
            </div>
            <div className="signal-timestamp">
              Updated {formatTime(lastUpdated || timestamp)}
              <button className="refresh-btn-minimal" onClick={refetch} disabled={loading}>
                <FaSync className={loading ? 'spinning' : ''} />
              </button>
            </div>
          </div>
          <h1 className="signal-title-minimal">AI Trading Signal</h1>
          <p className="signal-desc-minimal">Real-time market analysis powered by multi-timeframe technical indicators</p>
        </header>

        {/* Market Overview */}
        <section className="signal-section">
          <h2 className="section-heading">Market Overview</h2>
          <div className="market-overview-grid">
            {market_context?.regime && (
              <MarketRegimeCard
                regime={market_context.regime}
                notes={market_context.notes}
              />
            )}
            {suggestedAction && (
              <div className="suggested-action-card">
                <div className="action-label">Suggested Action</div>
                <div className="action-value">{suggestedAction}</div>
              </div>
            )}
          </div>
        </section>

        {/* Asset Analysis */}
        <section className="signal-section">
          <h2 className="section-heading">Asset Analysis</h2>
          <div className="assets-grid">
            {btc && <AssetAnalysisCard asset="BTC" data={transformAssetData(btc)} />}
            {avax && <AssetAnalysisCard asset="AVAX" data={transformAssetData(avax)} />}
          </div>
        </section>

        {/* Trading Decision */}
        {combined && (
          <section className="signal-section">
            <h2 className="section-heading">Trading Decision</h2>
            <div className="trading-decision-card">
              {suggestedPosition?.side && (
                <div className="decision-item">
                  <span className="decision-label">Position</span>
                  <span className="decision-value">{suggestedPosition.side}</span>
                </div>
              )}
              {riskInfo?.setup_grade && (
                <div className="decision-item">
                  <span className="decision-label">Setup Grade</span>
                  <span className="decision-value">{riskInfo.setup_grade}</span>
                </div>
              )}
              {managementPlan && (
                <div className="decision-item full-width">
                  <span className="decision-label">Management Plan</span>
                  <span className="decision-value">{managementPlan}</span>
                </div>
              )}
              {suggestedPosition?.entry_zone?.from && suggestedPosition?.entry_zone?.to && (
                <div className="decision-item">
                  <span className="decision-label">Entry Zone</span>
                  <span className="decision-value">
                    ${suggestedPosition.entry_zone.from} - ${suggestedPosition.entry_zone.to}
                  </span>
                </div>
              )}
              {suggestedPosition?.take_profit_primary && (
                <div className="decision-item">
                  <span className="decision-label">Take Profit</span>
                  <span className="decision-value">${suggestedPosition.take_profit_primary}</span>
                </div>
              )}
              {suggestedPosition?.stop_loss && (
                <div className="decision-item">
                  <span className="decision-label">Stop Loss</span>
                  <span className="decision-value stop-loss">${suggestedPosition.stop_loss}</span>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Invalidations */}
        {invalidations && invalidations.length > 0 && (
          <section className="signal-section">
            <h2 className="section-heading">Invalidation Conditions</h2>
            <div className="invalidations-card">
              <p className="invalidations-intro">
                The signal is invalidated if any of the following conditions occur:
              </p>
              <ul className="invalidations-list">
                {invalidations.map((item, index) => (
                  <li key={index}>
                    <FaExclamationTriangle />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Disclaimer */}
        <section className="signal-section">
          <div className="disclaimer-card">
            <FaExclamationTriangle />
            <div>
              <strong>Risk Disclaimer</strong>
              <p>
                Trading cryptocurrencies involves significant risk and can result in the loss of your invested capital.
                These signals are for informational purposes only and should not be considered financial advice.
                Always do your own research and never invest more than you can afford to lose.
              </p>
            </div>
          </div>
        </section>
      </motion.div>
    </div>
  );
};

export default Signal;
